import { useMemo, useState } from "react";
import {
  computeBatchStats,
  downloadAllBatchesReconcile,
  downloadBatchReconcile,
  downloadBatchRemainder,
  downloadBatchStats,
} from "@/lib/codePoolExport";
import { formatFullDate } from "@/lib/format";
import type { CodeExportLog, LicenseCode } from "@/pages/admin/tools/hooks/useLicenseTools";

interface BatchRedeemStatsProps {
  codes: LicenseCode[];
  exportLogs?: CodeExportLog[];
  notify: (text: string, tone?: "success" | "error") => void;
  onLogExport?: (batchId: string, count: number, kind?: string) => Promise<void>;
}

const PREVIEW_COUNT = 6;

// 激活码池「核销统计」：按批次汇总已核销 / 未核销 / 已停用，方便发码方逐批对账。
export default function BatchRedeemStats({ codes, exportLogs, notify, onLogExport }: BatchRedeemStatsProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const batches = useMemo(() => computeBatchStats(codes), [codes]);

  // 每个批次最近一次「未核销清单」导出留痕（导出人 + 时间），用于对账追溯。
  const latestExport = useMemo(() => {
    const map = new Map<string, CodeExportLog>();
    (exportLogs ?? []).forEach((log) => {
      const prev = map.get(log.batch_id);
      if (!prev || log.created_at > prev.created_at) map.set(log.batch_id, log);
    });
    return map;
  }, [exportLogs]);

  const unusedByBatch = useMemo(() => {
    const map = new Map<string, string[]>();
    codes.forEach((row) => {
      if (row.status !== "unused") return;
      const key = row.batch_id || "(无批次)";
      const list = map.get(key);
      if (list) list.push(row.code);
      else map.set(key, [row.code]);
    });
    return map;
  }, [codes]);

  const totals = useMemo(() => {
    const total = codes.length;
    const used = codes.filter((item) => item.status === "used").length;
    const unused = codes.filter((item) => item.status === "unused").length;
    const disabled = codes.filter((item) => item.status === "disabled").length;
    const rate = total > 0 ? Math.round((used / total) * 100) : 0;
    return { total, used, unused, disabled, rate, batchCount: batches.length };
  }, [codes, batches]);

  const visible = expanded ? batches : batches.slice(0, PREVIEW_COUNT);

  // 一键复制某批次里「还没被核销」的激活码，补发时直接拿去用。
  const copyBatchRemainder = async (batchId: string, productName: string) => {
    const list = unusedByBatch.get(batchId) ?? [];
    if (list.length === 0) {
      notify("该批次没有未核销的激活码", "error");
      return;
    }
    try {
      await navigator.clipboard.writeText(list.join("\n"));
      notify(`已复制「${productName || batchId}」未核销余量 ${list.length} 个`, "success");
    } catch {
      notify("复制失败，请手动选择复制", "error");
    }
  };

  // 按批次下载「还没被核销」的激活码清单，方便发码方存档或批量导入邮件系统。
  const downloadBatchRemainderList = async (batchId: string, productName: string) => {
    const count = downloadBatchRemainder(codes, batchId);
    if (count === 0) {
      notify("该批次没有未核销的激活码", "error");
      return;
    }
    notify(`已下载「${productName || batchId}」未核销清单 ${count} 个，并记录导出留痕`, "success");
    try {
      await onLogExport?.(batchId, count, "remainder");
    } catch {
      // 留痕写入失败不影响已完成的下载
    }
  };

  // 「批次对账包」：把同一批次的「未核销 + 已核销」两段清单合到一个 CSV，一次导出整批发放与核销现状。
  const downloadBatchReconcilePackage = async (batchId: string, productName: string) => {
    const { unused, used } = downloadBatchReconcile(codes, batchId, productName);
    if (unused === 0 && used === 0) {
      notify("该批次暂无可对账的激活码", "error");
      return;
    }
    notify(
      `已导出「${productName || batchId}」批次对账包（未核销 ${unused} · 已核销 ${used}），并记录导出留痕`,
      "success",
    );
    try {
      await onLogExport?.(batchId, unused + used, "reconcile");
    } catch {
      // 留痕写入失败不影响已完成的下载
    }
  };

  const handleExport = () => {
    if (batches.length === 0) {
      notify("暂无可导出的批次", "error");
      return;
    }
    downloadBatchStats(batches);
    notify(`已导出 ${batches.length} 个批次的核销统计`, "success");
  };

  // 「全部批次对账包」：一次把所有批次的发放与核销现状打包成一个文件，适合整体归档。
  // 由确认弹窗核对范围后调用。
  const runAllReconcile = async () => {
    setConfirmAll(false);
    if (batches.length === 0) {
      notify("暂无可对账的批次", "error");
      return;
    }
    const { batches: batchCount, unused, used } = downloadAllBatchesReconcile(codes, batches);
    notify(
      `已导出全部 ${batchCount} 个批次对账包（未核销 ${unused} · 已核销 ${used}），并记录导出留痕`,
      "success",
    );
    try {
      await onLogExport?.("全部批次", unused + used, "reconcile-all");
    } catch {
      // 留痕写入失败不影响已完成的下载
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-file-chart-line" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">核销统计 · 按批次对账</h2>
            <p className="text-xs text-foreground-500">查看每个批次已核销 / 未核销多少，导出后即可逐批核对发码发放情况。</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-download-2-line" />
            </span>
            导出统计
          </button>
          <button
            type="button"
            onClick={() => {
              if (batches.length === 0) {
                notify("暂无可对账的批次", "error");
                return;
              }
              setConfirmAll(true);
            }}
            title="一次导出所有批次的未核销 + 已核销明细，适合整体归档核对"
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-archive-2-line" />
            </span>
            导出全部批次对账包
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "批次数量", value: totals.batchCount, icon: "ri-stack-line", tone: "secondary" },
          { label: "已核销", value: totals.used, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "未核销", value: totals.unused, icon: "ri-radar-line", tone: "accent" },
          { label: "已停用", value: totals.disabled, icon: "ri-forbid-2-line", tone: "secondary" },
        ].map((card) => {
          const toneClass: Record<string, string> = {
            primary: "bg-primary-100 text-primary-700",
            secondary: "bg-secondary-100 text-secondary-900",
            accent: "bg-accent-100 text-accent-700",
          };
          return (
            <div key={card.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-600">{card.label}</span>
                <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                  <i className={`${card.icon} text-base`} />
                </span>
              </div>
              <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">{card.value}</p>
            </div>
          );
        })}
      </div>

      {batches.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-file-chart-line text-xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">还没有可统计的批次</p>
          <p className="text-xs text-foreground-500">先在上方生成一批原始激活码，这里就会按批次汇总核销情况。</p>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[880px] text-sm">
            <thead>
              <tr className="bg-background-100 text-left text-xs font-medium text-foreground-600">
                <th className="px-4 py-3">批次</th>
                <th className="px-4 py-3">产品 / 备注</th>
                <th className="px-4 py-3">已核销 / 总数</th>
                <th className="px-4 py-3">未核销</th>
                <th className="px-4 py-3">已停用</th>
                <th className="px-4 py-3">核销率</th>
                <th className="px-4 py-3">最近核销</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((batch) => {
                const exportInfo = latestExport.get(batch.batchId);
                return (
                <tr key={batch.batchId} className="border-t border-background-200">
                  <td className="px-4 py-3">
                    <p className="font-mono text-xs text-foreground-900">{batch.batchId}</p>
                    <p className="text-[11px] text-foreground-400">{formatFullDate(batch.createdAt)}</p>
                    {exportInfo && (
                      <p className="mt-0.5 flex items-center gap-1 text-[11px] text-foreground-400">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-history-line" />
                        </span>
                        最近导出：{exportInfo.exported_by_name || "—"} · {formatFullDate(exportInfo.created_at)}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-foreground-900">{batch.productName || "通用"}</p>
                    <p className="text-[11px] text-foreground-500">{batch.note || "—"}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground-950">{batch.used}</span>
                    <span className="text-foreground-500"> / {batch.total}</span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{batch.unused}</td>
                  <td className="px-4 py-3 text-foreground-500">{batch.disabled}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-background-200">
                        <div
                          className="h-full rounded-full bg-primary-500"
                          style={{ width: `${batch.redeemedRate}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-foreground-800">{batch.redeemedRate}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground-500">
                    {batch.lastUsedAt ? formatFullDate(batch.lastUsedAt) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={batch.unused === 0}
                        onClick={() => void copyBatchRemainder(batch.batchId, batch.productName)}
                        title="复制本批次尚未核销的激活码，用于补发"
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-file-copy-line" />
                        </span>
                        复制余量 {batch.unused}
                      </button>
                      <button
                        type="button"
                        disabled={batch.unused === 0}
                        onClick={() => void downloadBatchRemainderList(batch.batchId, batch.productName)}
                        title="下载本批次尚未核销的激活码清单，用于存档或导入邮件系统"
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-download-2-line" />
                        </span>
                        下载清单
                      </button>
                      <button
                        type="button"
                        onClick={() => void downloadBatchReconcilePackage(batch.batchId, batch.productName)}
                        title="导出该批次的未核销 + 已核销两段表，一次核对整批发放与核销现状"
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-archive-2-line" />
                        </span>
                        批次对账包
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {batches.length > PREVIEW_COUNT && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </span>
            {expanded ? "收起批次" : `展开全部 ${batches.length} 个批次`}
          </button>
        </div>
      )}

      {confirmAll && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setConfirmAll(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-archive-2-line text-xl" />
              </span>
              <div className="min-w-0">
                <h3 className="font-heading text-base font-semibold text-foreground-950">确认导出全部批次对账包</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">
                  本次将把所有批次打进同一个 CSV，导出前请核对范围。
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-background-200 bg-background-100 p-3 text-center">
                <p className="text-[11px] text-foreground-500">批次数量</p>
                <p className="mt-1 font-heading text-xl font-semibold text-foreground-950">{totals.batchCount}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-3 text-center">
                <p className="text-[11px] text-foreground-500">未核销</p>
                <p className="mt-1 font-heading text-xl font-semibold text-foreground-950">{totals.unused}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-3 text-center">
                <p className="text-[11px] text-foreground-500">已核销</p>
                <p className="mt-1 font-heading text-xl font-semibold text-foreground-950">{totals.used}</p>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-xs text-foreground-600">
              <span className="flex h-4 w-4 items-center justify-center text-accent-700">
                <i className="ri-file-list-3-line" />
              </span>
              合计将包含 <span className="font-medium text-foreground-900">{totals.unused + totals.used}</span> 条明细记录。
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAll(false)}
                className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void runAllReconcile()}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-download-2-line" />
                </span>
                确认导出
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}